import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import { useProfileActions } from '../components/ProfileCard';
import { Avatar, EmptyState, Loader } from '../components/UI';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { clock, timeAgo } from '../utils/format';

const STARTERS = ['Hi! I loved reading your profile 😊', 'How do you manage work-life balance with your shifts?', 'What do you enjoy doing on a day off?', 'Would you like to connect our families?'];

function Thread({ id, onSent }) {
  const { toast } = useApp();
  const { sendInterest } = useProfileActions();
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const endRef = useRef(null);
  const lastAt = useRef(null);

  const load = useCallback(async (initial) => {
    try {
      const q = !initial && lastAt.current ? `?after=${encodeURIComponent(lastAt.current)}` : '';
      const d = await api(`/chat/${id}${q}`);
      setData((prev) => {
        const msgs = initial || !prev ? d.messages : [...prev.messages, ...d.messages.filter((m) => !prev.messages.some((x) => x.id === m.id))];
        if (msgs.length) lastAt.current = msgs[msgs.length - 1].at;
        return { ...d, messages: msgs };
      });
    } catch (e) {
      if (initial) setData({ error: e.message });
    }
  }, [id]);

  useEffect(() => {
    lastAt.current = null;
    setData(null);
    load(true);
    const t = setInterval(() => load(false), 3000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages?.length]);

  const send = async (body = text) => {
    const msg = body.trim();
    if (!msg) return;
    setText('');
    try {
      const r = await api(`/chat/${id}`, { method: 'POST', body: { text: msg } });
      setData((d) => ({ ...d, messages: [...d.messages, r.message] }));
      lastAt.current = r.message.at;
      onSent?.();
    } catch (e) {
      setText(msg);
      toast(e.message, 'error');
    }
  };

  if (!data) return <Loader label="Loading conversation…" />;
  if (data.error) return <EmptyState icon="chat" title="Chat unavailable" text={data.error} />;
  const p = data.profile;
  const locked = !data.access?.ok;

  // Group messages under date separators.
  let lastDay = '';
  return (
    <div className="thread">
      <header className="thread__head">
        <Link to="/messages" className="icon-btn only-mobile" aria-label="Back"><Icon name="chevronLeft" /></Link>
        <Link to={`/profile/${p.profileId}`} className="thread__who">
          <Avatar src={p.photo} name={p.name} size={44} online={p.online} locked={p.photoLocked} />
          <span>
            <strong>{p.name}</strong>
            <small className="muted">{p.lastSeen || `${p.age} yrs · ${p.city}`}</small>
          </span>
        </Link>
        <Link to={`/kundli/${p.profileId}`} className="icon-btn" aria-label="Kundli"><Icon name="moon" /></Link>
        <Link to={`/profile/${p.profileId}`} className="icon-btn" aria-label="Profile"><Icon name="user" /></Link>
      </header>

      <div className="thread__body">
        {!data.messages.length && (
          <motion.div className="thread__intro" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Avatar src={p.photo} name={p.name} size={84} />
            <h3>Say hello to {p.firstName?.replace('Dr. ', '')}</h3>
            <p className="muted">A thoughtful first message makes all the difference.</p>
          </motion.div>
        )}
        {data.messages.map((m) => {
          const day = new Date(m.at).toDateString();
          const sep = day !== lastDay;
          lastDay = day;
          return (
            <div key={m.id}>
              {sep && <div className="thread__day"><span>{day === new Date().toDateString() ? 'Today' : new Date(m.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span></div>}
              <motion.div
                className={`bubble ${m.mine ? 'bubble--me' : ''}`}
                initial={{ opacity: 0, y: 12, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: 'spring', stiffness: 400, damping: 28 }}
              >
                {m.text}
                <small>{clock(m.at)} {m.mine && <Icon name="check" size={12} stroke={2.4} className={m.read ? 'read' : ''} />}</small>
              </motion.div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {locked ? (
        <div className="thread__gate">
          <Icon name="lock" size={20} />
          <div>
            <strong>{data.access.reason === 'blocked' ? 'Messaging unavailable' : 'Chat opens when your interest is accepted'}</strong>
            {data.access.reason !== 'blocked' && <small className="muted">Or go Premium to message anyone directly.</small>}
          </div>
          {data.access.reason !== 'blocked' && (
            <div className="row-actions">
              <button className="btn btn--primary btn--sm" onClick={() => sendInterest(p)}>Send interest</button>
              <Link to="/premium" className="btn btn--gold btn--sm">Premium</Link>
            </div>
          )}
        </div>
      ) : (
        <>
          {!data.messages.length && (
            <div className="starters">
              {STARTERS.map((s) => <button key={s} className="chip" onClick={() => send(s)}>{s}</button>)}
            </div>
          )}
          <form className="composer" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`Message ${p.firstName?.replace('Dr. ', '')}…`} maxLength={2000} />
            <motion.button whileTap={{ scale: 0.85 }} className="composer__send" disabled={!text.trim()} aria-label="Send">
              <Icon name="send" size={19} />
            </motion.button>
          </form>
        </>
      )}
    </div>
  );
}

export default function Messages() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshCounts } = useApp();
  const [list, setList] = useState(null);

  const loadList = useCallback(() => api('/chat').then((d) => setList(d.items)).catch(() => setList([])), []);
  useEffect(() => {
    loadList();
    const t = setInterval(loadList, 5000);
    return () => clearInterval(t);
  }, [loadList]);
  useEffect(() => {
    refreshCounts();
  }, [id, list?.length, refreshCounts]);

  return (
    <motion.main className={`messages ${id ? 'has-thread' : ''}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <aside className="convos">
        <div className="convos__head">
          <h1>Chats</h1>
          <Link to="/interests?box=accepted" className="link">Connections</Link>
        </div>
        {!list ? (
          <Loader label="" />
        ) : !list.length ? (
          <EmptyState icon="chat" title="No chats yet" text="Once an interest is accepted, you can start chatting here." action={<Link to="/matches" className="btn btn--primary btn--sm">Find matches</Link>} />
        ) : (
          <motion.div initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}>
            {list.map((c) => (
              <motion.button
                key={c.profile.profileId}
                variants={{ hidden: { opacity: 0, x: -16 }, show: { opacity: 1, x: 0 } }}
                className={`convo ${id === c.profile.profileId ? 'is-active' : ''}`}
                onClick={() => navigate(`/messages/${c.profile.profileId}`)}
              >
                <Avatar src={c.profile.photo} name={c.profile.name} size={50} online={c.profile.online} locked={c.profile.photoLocked} />
                <span className="convo__text">
                  <span className="convo__top"><strong>{c.profile.name}</strong><small>{timeAgo(c.last.at)}</small></span>
                  <span className={`convo__last ${c.unread ? 'is-unread' : ''}`}>{c.last.mine && 'You: '}{c.last.text}</span>
                </span>
                {c.unread > 0 && <span className="badge badge--static">{c.unread}</span>}
              </motion.button>
            ))}
          </motion.div>
        )}
      </aside>
      <section className="messages__pane">
        {id ? (
          <Thread key={id} id={id} onSent={loadList} />
        ) : (
          <EmptyState icon="chat" title="Pick a conversation" text="Choose a chat on the left, or start one from any profile you've connected with." />
        )}
      </section>
    </motion.main>
  );
}
